import { all, A, Br, Button, Checkbox, Div, Label, LI, Input, RadioInput, RangeInput, Span, UL, H2, H3, VString, TextArea, and, not, reactive, Hr } from 'alkali'
import { selectedStudies, studySelections, structuredQuery, analysisSelections } from 'model/SearchQuery'
import { questionToText } from 'analysis/Question'
import { screening, hasScreeningAccess, getArticleId, getArticleIdOfVote, voteForArticle } from 'model/Screening'
import { activeTab } from 'model/StudyCollection'
import Dropdown from 'components/generic/Dropdown'
import Icon from 'components/Icon'
import * as arrowDown from 'icons/arrow-down-1.svg'
import * as closeIcon from 'icons/close.svg'
import './ScreeningPane.scss'
import PicoTermsContainer from 'components/screening/PicoTermsContainer'

const weightingAccess = reactive(app.user).to((user) => Boolean(user?.FeatureConfig?.Debug?.Enabled))

export class ScreeningPane extends Div('.study-attributes') {
	ready(properties) {
		let includeExpanded = reactive(false)
		let includeMaybeReason = reactive(false)
		
		let moveToNext = properties.moveToNext
		let { study, studyList, annotModel } = properties
		
		let vote = screening.to((screening) =>
		screening?.votes.find((vote) => getArticleIdOfVote(vote) == getArticleId(study))
		).valueOf()
		let excludedReason = reactive(vote?.reason || vote?.meta?.reason)
		let flagType = vote?.type
		let rawWeighting = studySelections.property(study.id).property('weighting')
		let weighting = rawWeighting.to(weighting =>
			weighting > -1 ? weighting : 100,
			newWeighting => rawWeighting.put(newWeighting))
			let notes = reactive(vote?.notes || vote?.meta?.notes || '')
			let parentStudy = study.parentStudy || study
			let publicationList = [ study, ...([parentStudy, ...(parentStudy.additionalStudies || [])]).filter(publication => publication != study) ]
			
			const isScreening = analysisSelections.to(({ screeningId }) => Boolean(screeningId && hasScreeningAccess))
			const WeightingControl = Div('.study-weighting', {
				title: 'In meta-analysis, studies are weighted by inverse variance, and by default this is based only on observational variance and rough heterogeneity estimates, but applying a partial weight based on better informed estimates of heterogeneity can provide much more robust and accurate analysis. DocAnalytics\'s matching probabilities is a good, unbiased choice for partial weighting as it incorporates much more study-specific information to estimate heterogeneity, and is significantly better than only using the random effects estimates which were based only between-study variations.',
			}, [
				H3(['Slide to left to give partial weight to this study in the analysis']),
				Div('.partial-weight', ['Partial'] ),
				Div('.full-weight', ['Full'] ),
				RangeInput({
					valueAsNumber: weighting,
					step: 5,
				}),
			])
		this.append(
			activeTab.valueOf() == 'screening' ? ([
				H2('.screening-title', ['Screening Status']),
				Div('.flex-container', [
					Div('.study-select-options', { display: and(hasScreeningAccess, activeTab.to(tab => tab == 'screening')) }, [
						Div('.study-select-option', [
							Div('.study-select-details', [
								Button(
									'.include-study',
									{
										classes: {
											selected: vote?.type == 'Include',
										},
										onclick: async (event) => {
											if (!event.fromDropdown) {
												// flag the primary selected article as an include
												await screening.flag({
													type: 'Include',
													notes,
													articleIds: [getArticleId(study)],
												}, study).catch(error => alert(error))
												// if remaining articles exist, exclude them
												if (publicationList.length > 1) {
													for (let listStudy of publicationList) {
														if (getArticleId(listStudy) == getArticleId(study)) {
															await screening.flag({
																type: 'Include',
																notes,
																articleIds: [getArticleId(listStudy)],
															}, listStudy).catch(error => console.log(error))
														}
													}
													const remainingStudies = publicationList.filter(listStudy => getArticleId(listStudy) !== getArticleId(parentStudy) && getArticleId(listStudy) !== getArticleId(study))
													for (let additionalStudy of remainingStudies) {
														await screening.flag({
															type: "Overlapping population",
															reason: "Overlapping population",
															notes,
															articleIds: [getArticleId(additionalStudy)], 

														}).catch(error => console.log(error))
													}
												}
												moveToNext()
											}
											if (study.analysisModel?.analyzable.get('isAnalyzable'))
												studyList.select(study.id, null, true)	
										},
									},
									weightingAccess.valueOf() ?
									[
										Span([(vote?.type == 'Include') ? 'Included' : 'Include',
										weighting.to((weighting) =>
											weighting == 100 || isNaN(weighting)
												? null
												: '(' + Math.round(weighting) + '%)'
										)
										]),
										Div('.dropdown-side', {
											display: weightingAccess,
											onclick(event) {
												event.fromDropdown = true
												includeExpanded.put(!includeExpanded.valueOf())
											},
										}, [Icon('.icon-dropdown', { icon: arrowDown })]),
									] :
									[vote?.type == 'Include' ? 'Included' : 'Include'],
								),
								weightingAccess.valueOf() ? WeightingControl({ display: includeExpanded }) : null,
							])
						]),
						Div('.study-select-option', [
							Div('.study-select-details', [
								Button(
									'.maybe-study',
									[
										'Uncertain',
										/*	Span('.dropdown-side', [Icon('.icon-dropdown', { icon: arrowDown })], {
											onclick(event) {
												event.fromDropdown = true
												includeMaybeReason.put(!includeMaybeReason.valueOf())
											},
										}),*/
									],
									{
										classes: {
											selected: vote?.type == 'Uncertain',
										},
										onclick(event) {
											if (!event.fromDropdown) {
												screening.flag({
													type: 'Uncertain',
													notes,
													reason: includeMaybeReason.valueOf(),
													articleIds: publicationList.map(getArticleId),
												})
												moveToNext()
											}
										},
									}
								),
								Div(
									'.maybe-reason',
									{
										display: includeMaybeReason,
									},
									[TextArea({ placeholder: 'Screening notes'})]
								),
							]),
						]),
						Div('.study-select-option', [
							Div('.study-select-details', [
								Button(
									'.reject-study',
									[
										excludedReason.to((excludedReason) =>
										excludedReason ? Span('.include-reason', ['Excluded:', Span(excludedReason)]) : Span('Exclude')
										),
										Div('.dropdown-side', [Icon('.icon-dropdown', { icon: arrowDown })], {
											onclick(event) {
												event.stopPropagation()
												excludedReasonDropdown.voteImmediately = false
												excludedReasonDropdown.show(event)
											},
										}),
									],
									{
										classes: {
											selected: vote?.type.startsWith('Wrong'),
										},
										onclick: (event) => {
											if (excludedReason.valueOf()) {
												voteToExclude()
											} else {
												excludedReasonDropdown.voteImmediately = true
												excludedReasonDropdown.show(event)
											}
										},
									}
								),
							]),
						]),
						/*			Div('.study-select-option', [ // TODO: Where do we want to do trial grouping?
						Div('.study-select-details', [
							Button(['Group this publication with trial']),
							SelectivityDropdown({
								placeholder:'Select study',
								items: [
									{ id: 1, text: 'No existing trial (new/separate trial)' },
									{ id: 2, text: '2009 CHARM' }
								]
							}),
						]),
					]),*/
					]),
					Div('.screening-notes', { display: and(hasScreeningAccess, activeTab.to(tab => tab == 'screening')) }, [
						TextArea({ placeholder: 'Notes', value: notes })]
					),
				]),
				Hr(),
			]) : null,
			Div('.study-select-options', {
				display: and(not(hasScreeningAccess), weightingAccess),
			}, [
				H2('Weighting'),
				WeightingControl,
			]),
			new PicoTermsContainer({ study, studyList, publicationList, excludedReason, annotModel })
		)
		let voteToExclude = () => {
			this.studyList.exclude(study.id)
			screening.flag({
				type: excludedReason.valueOf().split(':')[0],
				reason: excludedReason.valueOf(),
				notes,
				articleIds: publicationList.map(getArticleId),
			})
			moveToNext()
		}
		let excludedReasonDropdown = new (class extends Dropdown.with('.excluded-dropdown') {
			ready({ outcomes }) {
				this.items = [
					{
						content: Div(
							'.select-exclude-reason',
							{
								classes: { selected: excludedReason.to((reason) => reason == 'Wrong study design') },
							},
							['Wrong study design']
						),
						onclick: () => {
							excludedReason.put('Wrong study design')
							voteToExclude()
						},
					},
					{
						content: Div(
							'.select-exclude-reason',
							{
								classes: {
									selected: excludedReason.to((reason) => reason == 'Wrong population'),
								},
							},
							['Wrong population']
						),
						onclick() {
							excludedReason.put('Wrong population')
							voteToExclude()
						},
					},
					{
						content: Div(
							'.select-exclude-reason',
							{
								classes: {
									selected: excludedReason.to((reason) => reason == 'Wrong intervention'),
								},
							},
							['Wrong intervention']
						),
						onclick() {
							excludedReason.put('Wrong intervention')
							voteToExclude()
						},
					},
					{
						content: Div(
							'.select-exclude-reason',
							{
								classes: {
									selected: excludedReason.to((reason) => reason == 'Wrong outcome'),
								},
							},
							['Wrong outcome']
						),
						onclick() {
							excludedReason.put('Wrong outcome')
							voteToExclude()
						},
					},
				]
			}
		})()
	}
}
